import React, { useState, useEffect } from 'react';
import { 
  Video, 
  BookOpen, 
  HelpCircle, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'react-toastify';
import VideoUploadForm from '../VideoUploadForm';
import EBookUploadForm from '../EBookUploadForm';
import QnAUploadForm from '../QnAUploadForm';
import { getRankLabel, getRankStyle } from '../../utils/rankStyles';
import './LibraryAdmin.scss';

const LibraryAdmin = () => {
  const [activeTab, setActiveTab] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [qnas, setQnas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRank, setSelectedRank] = useState('all');
  
  // Modal states
  const [videoUploadOpen, setVideoUploadOpen] = useState(false);
  const [ebookUploadOpen, setEbookUploadOpen] = useState(false);
  const [qnaUploadOpen, setQnaUploadOpen] = useState(false);
  
  // Editing states
  const [editingVideo, setEditingVideo] = useState(null);
  const [editingEbook, setEditingEbook] = useState(null);
  const [editingQna, setEditingQna] = useState(null);

  const ranks = [
    { value: 'all', label: 'Toate rangurile' },
    ...[
      'basic',
      'executive',
      'elite',
      'premier',
      'silver',
      'gold',
      'platinum',
      'diamond',
      'blue_diamond',
      'presidential_diamond',
    ].map((value) => ({ value, label: getRankLabel(value) }))
  ];

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      // Import services for API calls
      const videoService = (await import('../../services/videoService')).default;
      const eBookService = (await import('../../services/eBookService')).default;
      const qnaService = (await import('../../services/qnaService')).default;
      
      // Load real data from APIs
      const [videoData, ebookData, qnaData] = await Promise.all([
        videoService.getVideos().catch(() => []),
        eBookService.getEBooks().catch(() => []),
        qnaService.getQnAs().catch(() => [])
      ]);
      
      const normalizedVideos = Array.isArray(videoData)
        ? videoData
        : Array.isArray(videoData?.data)
          ? videoData.data
          : [];
      setVideos(normalizedVideos);
      setEbooks(Array.isArray(ebookData) ? ebookData : []);
      setQnas(Array.isArray(qnaData) ? qnaData : []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Eroare la încărcarea datelor');
      
      setVideos([]);
      setEbooks([]);
      setQnas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContent = () => {
    switch (activeTab) {
      case 'videos':
        setEditingVideo(null);
        setVideoUploadOpen(true);
        break;
      case 'ebooks':
        setEditingEbook(null);
        setEbookUploadOpen(true);
        break;
      case 'qnas':
        setEditingQna(null);
        setQnaUploadOpen(true);
        break;
      default:
        break;
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoUploadOpen(true);
  };

  const handleEditEbook = (ebook) => {
    setEditingEbook(ebook);
    setEbookUploadOpen(true);
  };

  const handleEditQna = (qna) => {
    setEditingQna(qna);
    setQnaUploadOpen(true);
  };

  const handleSaveVideo = async (videoData) => {
    try {
      console.log('Saving video data:', videoData);
      
      // Map rank to category for the public library
      const rankToCategoryMap = {
        'Inceput': 'Început',
        'basic': 'Început', 
        'Echipa': 'Echipă',
        'team_member': 'Echipă',
        'Premier': 'Ranks',
        'premier': 'Ranks',
        'Silver': 'Ranks',
        'silver': 'Ranks',
        'Gold': 'Ranks', 
        'gold': 'Ranks',
        'Platinum': 'Ranks',
        'platinum': 'Ranks',
        'Diamond': 'Ranks',
        'diamond': 'Ranks',
        'Blue Diamond': 'Ranks',
        'blue_diamond': 'Ranks',
        'Presidential Diamond': 'Ranks',
        'presidential_diamond': 'Ranks',
        'Business': 'Business'
      };
      
      // Add category based on rank
      const processedVideoData = {
        ...videoData,
        category: rankToCategoryMap[videoData.requiredRank] || videoData.category || 'Început'
      };
      
      // Import video service for API calls
      const videoService = (await import('../../services/videoService')).default;
      
      // Make actual API call to save the video
      if (editingVideo) {
        // Update existing video
        const response = await videoService.updateVideo(editingVideo.id, processedVideoData);
        console.log('Video updated:', response);
        
        // Update local state
        setVideos(prev => prev.map(v => v.id === editingVideo.id ? { ...processedVideoData, id: editingVideo.id } : v));
        toast.success('Video actualizat cu succes!');
      } else {
        // Create new video
        const response = await videoService.createVideo(processedVideoData);
        console.log('Video created:', response);
        
        // Update local state with the new video (include the ID from the response)
        const newVideo = {
          ...processedVideoData,
          id: response?.data?.id || response?.id || `video_${Date.now()}`
        };
        setVideos(prev => [...prev, newVideo]);
        toast.success('Video adăugat cu succes!');
      }
      
      setVideoUploadOpen(false);
      setEditingVideo(null);
      
      // Reload data to ensure consistency
      await loadData();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error('Eroare la salvarea videoclipului: ' + (error.message || 'Eroare necunoscută'));
      throw error;
    }
  };

  const handleSaveEbook = async (ebookData) => {
    try {
      const eBookService = (await import('../../services/eBookService')).default;
      
      if (editingEbook) {
        // Update existing eBook
        await eBookService.updateEBook(editingEbook.id, ebookData);
        toast.success('eBook actualizat cu succes');
      } else {
        // Create new eBook
        await eBookService.createEBook(ebookData);
        toast.success('eBook adăugat cu succes');
      }
      
      setEbookUploadOpen(false);
      setEditingEbook(null);
      
      // Refresh the eBooks list
      await loadData();
    } catch (error) {
      console.error('Error saving eBook:', error);
      toast.error('Eroare la salvarea eBook-ului');
    }
  };

  const handleSaveQna = async (qnaData) => {
    try {
      const qnaService = (await import('../../services/qnaService')).default;
      
      if (editingQna) {
        // Update existing Q&A
        await qnaService.updateQnA(editingQna.id, qnaData);
        toast.success('Q&A actualizat cu succes');
      } else {
        // Create new Q&A
        await qnaService.createQnA(qnaData);
        toast.success('Q&A adăugat cu succes');
      }
      
      setQnaUploadOpen(false);
      setEditingQna(null);
      
      // Refresh the Q&As list
      await loadData();
    } catch (error) {
      console.error('Error saving Q&A:', error);
      toast.error('Eroare la salvarea Q&A-ului');
    }
  };

  const formatDuration = (seconds) => {
    const roundedSeconds = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(roundedSeconds / 60);
    const remainingSeconds = roundedSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const detectVideoDuration = (videoUrl) => new Promise((resolve, reject) => {
    const media = document.createElement('video');
    const timeout = window.setTimeout(() => reject(new Error('Timeout la citirea duratei')), 12000);

    const cleanUp = () => {
      window.clearTimeout(timeout);
      media.removeAttribute('src');
      media.load();
    };

    media.preload = 'metadata';
    media.onloadedmetadata = () => {
      const duration = media.duration;
      cleanUp();
      Number.isFinite(duration) ? resolve(formatDuration(duration)) : reject(new Error('Durată invalidă'));
    };
    media.onerror = () => {
      cleanUp();
      reject(new Error('Metadatele video nu au putut fi citite'));
    };
    media.src = videoUrl;
  });

  const handleAutoDetectDuration = async () => {
    setLoading(true);
    try {
      const videoService = (await import('../../services/videoService')).default;
      const candidates = videos.filter(video => video.id && video.videoUrl);
      const results = await Promise.allSettled(candidates.map(async (video) => {
        const duration = await detectVideoDuration(video.videoUrl);
        await videoService.updateVideo(video.id, { ...video, duration });
        return { id: video.id, duration };
      }));
      const updates = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);

      if (!updates.length) {
        toast.info('Nu există videoclipuri cu metadate accesibile pentru actualizare');
        return;
      }

      setVideos(previous => previous.map(video => {
        const update = updates.find(item => item.id === video.id);
        return update ? { ...video, duration: update.duration } : video;
      }));
      toast.success(`Durata a fost actualizată pentru ${updates.length} videoclipuri`);
    } catch (error) {
      toast.error('Eroare la detectarea automată a duratei');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncLessons = async () => {
    setLoading(true);
    try {
      const videoService = (await import('../../services/videoService')).default;
      const updates = videos
        .map(video => ({
          video,
          lessons: Array.isArray(video.chapters)
            ? video.chapters.length
            : Array.isArray(video.lessons)
              ? video.lessons.length
              : Number(video.lessons) || 1
        }))
        .filter(({ video, lessons }) => video.id && video.lessons !== lessons);

      await Promise.all(updates.map(({ video, lessons }) => (
        videoService.updateVideo(video.id, { ...video, lessons })
      )));
      setVideos(previous => previous.map(video => {
        const update = updates.find(item => item.video.id === video.id);
        return update ? { ...video, lessons: update.lessons } : video;
      }));
      toast.success(updates.length
        ? `Au fost sincronizate ${updates.length} videoclipuri`
        : 'Numărul lecțiilor este deja sincronizat');
    } catch (error) {
      toast.error('Eroare la sincronizarea lecțiilor');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContent = async (id, type) => {
    if (window.confirm('Ești sigur că vrei să ștergi acest conținut?')) {
      try {
        switch (type) {
          case 'Video': {
            const videoService = (await import('../../services/videoService')).default;
            await videoService.deleteVideo(id);
            setVideos(videos.filter(v => v.id !== id));
            break;
          }
          case 'eBook': {
            const eBookService = (await import('../../services/eBookService')).default;
            await eBookService.deleteEBook(id);
            setEbooks(ebooks.filter(e => e.id !== id));
            break;
          }
          case 'Q&A': {
            const qnaService = (await import('../../services/qnaService')).default;
            await qnaService.deleteQnA(id);
            setQnas(qnas.filter(q => q.id !== id));
            break;
          }
          default:
            break;
        }
        toast.success(`${type} șters cu succes`);
      } catch (error) {
        console.error(`Error deleting ${type}:`, error);
        toast.error(`Eroare la ștergerea ${type.toLowerCase()}-ului`);
      }
    }
  };

  const openExternalResource = (url, unavailableMessage) => {
    if (!url) {
      toast.info(unavailableMessage);
      return;
    }

    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (openedWindow) openedWindow.opener = null;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} className="status-icon active" />;
      case 'draft':
        return <AlertCircle size={16} className="status-icon draft" />;
      default:
        return <Clock size={16} className="status-icon pending" />;
    }
  };

  const filteredVideos = videos.filter(video => {
    const matchesSearch = (video.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (video.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRank = selectedRank === 'all' ||
      (video.requiredRank || video.rank) === selectedRank;
    return matchesSearch && matchesRank;
  });

  const renderVideos = () => (
    <div className="library-content-section">
      <div className="content-header">
        <div className="header-controls">
          <div className="search-bar">
            <Search size={16} />
            <input
              type="text"
              placeholder="Caută videouri..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            value={selectedRank} 
            onChange={(e) => setSelectedRank(e.target.value)}
            className="rank-filter"
          >
            {ranks.map(rank => (
              <option key={rank.value} value={rank.value}>
                {rank.label}
              </option>
            ))}
          </select>
        </div>
        <div className="content-actions">
          <button 
            className="btn btn-outline"
            onClick={handleAutoDetectDuration}
            disabled={loading}
          >
            <Clock size={16} />
            Auto-detect durată
          </button>
          <button 
            className="btn btn-outline"
            onClick={handleSyncLessons}
            disabled={loading}
          >
            <CheckCircle size={16} />
            Sincronizare lecții
          </button>
          <button className="btn btn-primary" onClick={handleAddContent}>
            <Plus size={16} />
            Adaugă video
          </button>
        </div>
      </div>

      <div className="content-grid">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Se încarcă...</p>
          </div>
        ) : filteredVideos.length > 0 ? (
          filteredVideos.map(video => (
            <div key={video.id} className="content-card video-card">
              <div className="card-header">
                <div className="card-status">
                  {getStatusIcon(video.isActive !== false ? 'active' : 'draft')}
                  <span className={`status-text ${video.isActive !== false ? 'active' : 'draft'}`}>
                    {video.isActive !== false ? 'Activ' : 'Draft'}
                  </span>
                </div>
                <div className="card-actions">
                  <button
                    className="action-btn"
                    title="Vizualizează"
                    onClick={() => openExternalResource(video.videoUrl, 'Videoclipul nu are încă un URL disponibil')}
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    className="action-btn" 
                    title="Editează"
                    onClick={() => handleEditVideo(video)}
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="action-btn delete" 
                    title="Șterge"
                    onClick={() => handleDeleteContent(video.id, 'Video')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className="card-content">
                <h4 className="card-title">{video.title}</h4>
                <p className="card-description">{video.description}</p>
                
                <div className="card-meta">
                  <div className="meta-row">
                    <span className="meta-label">Durată:</span>
                    <span className="meta-value">{video.duration}</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Lecții:</span>
                    <span className="meta-value">{video.lessons}</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Vizualizări:</span>
                    <span className="meta-value">{video.viewCount || video.views || 0}</span>
                  </div>
                </div>
                
                <div className="card-footer">
                  <span 
                    className="rank-badge"
                    style={getRankStyle(video.requiredRank || video.rank)}
                  >
                    {ranks.find(r => r.value === (video.requiredRank || video.rank))?.label ||
                      video.requiredRank ||
                      video.rank}
                  </span>
                  <span className="category-badge">{video.category}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <Video size={48} />
            <h4>Niciun videoclip găsit</h4>
            <p>Adaugă primul videoclip pentru a începe</p>
            <button className="btn btn-primary" onClick={handleAddContent}>
              <Plus size={16} />
              Adaugă video
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderEbooks = () => {
    const filteredEbooks = ebooks.filter(ebook => {
      const matchesSearch = ebook.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           ebook.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRank = selectedRank === 'all' || ebook.requiredRank === selectedRank;
      return matchesSearch && matchesRank;
    });

    return (
      <div className="library-content-section">
        <div className="section-header">
          <div className="search-filters">
            <div className="search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Caută eBook-uri..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              className="filter-select"
              value={selectedRank}
              onChange={(e) => setSelectedRank(e.target.value)}
            >
              {ranks.map(rank => (
                <option key={rank.value} value={rank.value}>
                  {rank.label}
                </option>
              ))}
            </select>
          </div>
          <div className="section-actions">
            <button className="btn btn-primary" onClick={handleAddContent}>
              <Plus size={16} />
              Adaugă eBook
            </button>
          </div>
        </div>

        <div className="content-grid">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Se încarcă...</p>
            </div>
          ) : filteredEbooks.length > 0 ? (
            filteredEbooks.map(ebook => (
              <div key={ebook.id} className="content-card ebook-card">
                <div className="card-header">
                  <div className="card-status">
                    {getStatusIcon(ebook.isActive ? 'active' : 'draft')}
                    <span className={`status-text ${ebook.isActive ? 'active' : 'draft'}`}>
                      {ebook.isActive ? 'Activ' : 'Draft'}
                    </span>
                  </div>
                  <div className="card-actions">
                    {ebook.ebookUrl && (
                      <button
                        className="action-btn"
                        title="Descarcă"
                        onClick={() => openExternalResource(ebook.ebookUrl, 'Fișierul eBook nu este disponibil')}
                      >
                        <Download size={16} />
                      </button>
                    )}
                    <button 
                      className="action-btn" 
                      title="Editează"
                      onClick={() => handleEditEbook(ebook)}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="action-btn delete" 
                      title="Șterge"
                      onClick={() => handleDeleteContent(ebook.id, 'eBook')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="card-content">
                  <h4 className="card-title">{ebook.title}</h4>
                  <p className="card-description">{ebook.description}</p>
                  
                  <div className="card-metadata">
                    {ebook.pages && (
                      <div className="metadata-item">
                        <FileText size={14} />
                        <span>{ebook.pages} pagini</span>
                      </div>
                    )}
                    {ebook.category && (
                      <div className="metadata-item">
                        <span className="metadata-label">Categorie:</span>
                        <span>{ebook.category}</span>
                      </div>
                    )}
                    {ebook.requiredRank && (
                      <div className="metadata-item">
                        <span 
                          className="rank-badge"
                          style={getRankStyle(ebook.requiredRank)}
                        >
                          {ranks.find(r => r.value === ebook.requiredRank)?.label || ebook.requiredRank}
                        </span>
                      </div>
                    )}
                  </div>

                  {ebook.tags && ebook.tags.length > 0 && (
                    <div className="card-tags">
                      {ebook.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                      {ebook.tags.length > 3 && (
                        <span className="tag-more">+{ebook.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <BookOpen size={48} />
              <h4>Încă nu există eBook-uri</h4>
              <p>Adaugă primul eBook pentru a începe să construiești biblioteca ta educațională</p>
              <button className="btn btn-primary" onClick={handleAddContent}>
                <Plus size={16} />
                Adaugă primul eBook
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderQnAs = () => {
    const filteredQnas = qnas.filter(qna => {
      const matchesSearch = qna.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           qna.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRank = selectedRank === 'all' || qna.requiredRank === selectedRank;
      return matchesSearch && matchesRank;
    });

    return (
      <div className="library-content-section">
        <div className="section-header">
          <div className="search-filters">
            <div className="search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Caută Q&A..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              className="filter-select"
              value={selectedRank}
              onChange={(e) => setSelectedRank(e.target.value)}
            >
              {ranks.map(rank => (
                <option key={rank.value} value={rank.value}>
                  {rank.label}
                </option>
              ))}
            </select>
          </div>
          <div className="section-actions">
            <button className="btn btn-primary" onClick={handleAddContent}>
              <Plus size={16} />
              Adaugă Q&A
            </button>
          </div>
        </div>

        <div className="content-grid">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Se încarcă...</p>
            </div>
          ) : filteredQnas.length > 0 ? (
            filteredQnas.map(qna => (
              <div key={qna.id} className="content-card qna-card">
                <div className="card-header">
                  <div className="card-status">
                    {getStatusIcon(qna.isActive ? 'active' : 'draft')}
                    <span className={`status-text ${qna.isActive ? 'active' : 'draft'}`}>
                      {qna.isActive ? 'Activ' : 'Draft'}
                    </span>
                  </div>
                  <div className="card-actions">
                    <button
                      className="action-btn"
                      title="Vizualizează"
                      onClick={() => openExternalResource('/librarie', 'Pagina bibliotecii nu este disponibilă')}
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      className="action-btn" 
                      title="Editează"
                      onClick={() => handleEditQna(qna)}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="action-btn delete" 
                      title="Șterge"
                      onClick={() => handleDeleteContent(qna.id, 'Q&A')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="card-content">
                  <h4 className="card-title">{qna.title}</h4>
                  <p className="card-description">{qna.description}</p>
                  
                  <div className="card-metadata">
                    {qna.questions && (
                      <div className="metadata-item">
                        <HelpCircle size={14} />
                        <span>{qna.questions.length} întrebări</span>
                      </div>
                    )}
                    {qna.category && (
                      <div className="metadata-item">
                        <span className="metadata-label">Categorie:</span>
                        <span>{qna.category}</span>
                      </div>
                    )}
                    {qna.requiredRank && (
                      <div className="metadata-item">
                        <span 
                          className="rank-badge"
                          style={getRankStyle(qna.requiredRank)}
                        >
                          {ranks.find(r => r.value === qna.requiredRank)?.label || qna.requiredRank}
                        </span>
                      </div>
                    )}
                  </div>

                  {qna.tags && qna.tags.length > 0 && (
                    <div className="card-tags">
                      {qna.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                      {qna.tags.length > 3 && (
                        <span className="tag-more">+{qna.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <HelpCircle size={48} />
              <h4>Încă nu există secțiuni Q&A</h4>
              <p>Adaugă prima secțiune Q&A pentru a răspunde întrebărilor frecvente</p>
              <button className="btn btn-primary" onClick={handleAddContent}>
                <Plus size={16} />
                Adaugă prima secțiune Q&A
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="library-admin">
      <div className="library-header">
        <h2>Libraria mea Admin</h2>
        <p>Gestionează videouri, eBook-uri și secțiuni Q&A pentru platformă</p>
      </div>

      <div className="library-tabs">
        <button 
          className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          <Video size={16} />
          Videouri ({videos.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'ebooks' ? 'active' : ''}`}
          onClick={() => setActiveTab('ebooks')}
        >
          <BookOpen size={16} />
          eBook-uri ({ebooks.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'qnas' ? 'active' : ''}`}
          onClick={() => setActiveTab('qnas')}
        >
          <HelpCircle size={16} />
          Q&A ({qnas.length})
        </button>
      </div>

      <div className="library-content">
        {activeTab === 'videos' && renderVideos()}
        {activeTab === 'ebooks' && renderEbooks()}
        {activeTab === 'qnas' && renderQnAs()}
      </div>

      {/* Video Upload Form Modal */}
      <VideoUploadForm
        open={videoUploadOpen}
        editingVideo={editingVideo}
        onClose={() => {
          setVideoUploadOpen(false);
          setEditingVideo(null);
        }}
        onSave={handleSaveVideo}
      />

      {/* eBook Upload Form Modal */}
      <EBookUploadForm
        open={ebookUploadOpen}
        editingEbook={editingEbook}
        onClose={() => {
          setEbookUploadOpen(false);
          setEditingEbook(null);
        }}
        onSave={handleSaveEbook}
      />

      {/* Q&A Upload Form Modal */}
      <QnAUploadForm
        open={qnaUploadOpen}
        editingQna={editingQna}
        onClose={() => {
          setQnaUploadOpen(false);
          setEditingQna(null);
        }}
        onSave={handleSaveQna}
      />
    </div>
  );
};

export default LibraryAdmin;
